import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {
    CheckCircleIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";

type FieldConfig = {
    visible: boolean;
    required: boolean;
    reference?: boolean;
};

export type CustomFieldType = "text" | "number" | "date" | "textarea" | "select" | "radio" | "checkbox";

export type CustomFieldSection = "subject" | "request";

export type CustomFieldConfig = {
    id: string;
    label: string;
    type: CustomFieldType;
    section: CustomFieldSection;
    visible: boolean;
    required: boolean;
    placeholder?: string;
    options?: string[];
};

export type ServiceRequestConfig = {
    fields: Record<string, FieldConfig>;
    features: Record<string, boolean>;
    request: Record<string, FieldConfig>;
    customFields: CustomFieldConfig[];
};

type Props = {
    value?: Partial<ServiceRequestConfig>;
    onSave: (config: ServiceRequestConfig) => Promise<void> | void;
};

const DEFAULT_CONFIG: ServiceRequestConfig = {
    fields: {
        name: { visible: true, required: true },
        voterId: { visible: true, required: true },
        serviceRequestLevel: { visible: true, required: true, reference: false },
        aadhaar: { visible: false, required: false },
        phone: { visible: false, required: false },
        whatsapp: { visible: false, required: false },
        relativeName: { visible: true, required: false },
        caste: { visible: false, required: false },
        address: { visible: true, required: false },
        dob: { visible: true, required: false },
        age: { visible: true, required: false },
        gender: { visible: true, required: false },
        booth: { visible: true, required: false },
        voterPresence: { visible: true, required: false },
        otherAcVoter: { visible: true, required: false },
        familyMembers: { visible: true, required: false },
        camera: { visible: true, required: false },
    },
    features: {
        addFromVoterList: true,
        facialRecognition: true,
        voiceSearch: true,
    },
    request: {
        status: { visible: true, required: false },
        assignee: { visible: true, required: true },
        priority: { visible: true, required: false },
        location: { visible: false, required: false },
        ministry: { visible: true, required: false },
        assembly: { visible: true, required: false },
        requestedDate: { visible: true, required: false },
        additionalInformation: { visible: true, required: false },
        funds: { visible: true, required: false },
        rtc: { visible: true, required: false },
        attachments: { visible: true, required: false },
        serviceRequestType: { visible: true, required: true },
    },
    customFields: [],
};

const SUBJECT_FIELDS = [
    ["name", "Name"],
    ["voterId", "Voter ID"],
    ["serviceRequestLevel", "Service Request Level"],
    ["aadhaar", "Aadhaar"],
    ["phone", "Phone"],
    ["whatsapp", "WhatsApp"],
    ["relativeName", "Relative Name"],
    ["caste", "Caste"],
    ["address", "Address"],
    ["dob", "Date of Birth"],
    ["age", "Age"],
    ["gender", "Gender"],
    ["booth", "Booth"],
    ["voterPresence", "Voter Presence"],
    ["otherAcVoter", "Other AC Voter"],
    ["familyMembers", "Family Members"],
    ["camera", "Camera"],
] as const;

const REQUEST_FIELDS = [
    ["status", "Status"],
    ["assignee", "Assignee"],
    ["priority", "Priority"],
    ["location", "Location"],
    ["ministry", "Ministry"],
    ["assembly", "Assembly"],
    ["requestedDate", "Requested Date"],
    ["additionalInformation", "Additional Information"],
    ["funds", "Funds"],
    ["rtc", "RTC"],
    ["attachments", "Attachments"],
    ["serviceRequestType", "Service Request Type"],
] as const;

const FEATURE_FIELDS = [
    ["addFromVoterList", "Add From Voter List", "Allow users to add a voter directly from the voter list."],
    ["facialRecognition", "Facial Recognition", "Allow facial recognition while selecting the voter."],
    ["voiceSearch", "Voice Search", "Allow voice-based voter search."],
] as const;

const FIELD_TYPE_OPTIONS: Array<{ value: CustomFieldType; label: string; description: string }> = [
    { value: "text", label: "Text", description: "Short single-line text" },
    { value: "number", label: "Number", description: "Numeric value" },
    { value: "date", label: "Date", description: "Date picker" },
    { value: "textarea", label: "Long Text", description: "Multi-line text" },
    { value: "select", label: "Dropdown", description: "Choose one option" },
    { value: "radio", label: "Radio", description: "Choose one visible option" },
    { value: "checkbox", label: "Yes / No", description: "Boolean choice" },
];

const slugify = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .replace(/_+/g, "_");

const mergeConfig = (value?: Partial<ServiceRequestConfig>): ServiceRequestConfig => ({
    fields: {
        ...DEFAULT_CONFIG.fields,
        ...(value?.fields || {}),
    },
    features: {
        ...DEFAULT_CONFIG.features,
        ...(value?.features || {}),
    },
    request: {
        ...DEFAULT_CONFIG.request,
        ...(value?.request || {}),
    },
    customFields: Array.isArray(value?.customFields)
        ? value!.customFields!.map((field) => ({
              ...field,
              visible: field.visible !== false,
              required: field.required === true,
              options: Array.isArray(field.options) ? field.options : [],
          }))
        : [],
});

type CustomFieldDraft = {
    id: string;
    label: string;
    type: CustomFieldType;
    section: CustomFieldSection;
    placeholder: string;
    required: boolean;
    visible: boolean;
    options: string[];
};

const EMPTY_DRAFT: CustomFieldDraft = {
    id: "",
    label: "",
    type: "text",
    section: "request",
    placeholder: "",
    required: false,
    visible: true,
    options: [],
};

const ServiceRequestFormConfig = ({ value, onSave }: Props) => {
    const [config, setConfig] = useState<ServiceRequestConfig>(() => mergeConfig(value));
    const [saving, setSaving] = useState(false);
    const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
    const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
    const [draft, setDraft] = useState<CustomFieldDraft>(EMPTY_DRAFT);
    const [optionInput, setOptionInput] = useState("");
    const [customFieldError, setCustomFieldError] = useState("");

    useEffect(() => {
        setConfig(mergeConfig(value));
    }, [value]);

    const updateField = (
        section: "fields" | "request",
        field: string,
        key: keyof FieldConfig,
        fieldValue: boolean
    ) => {
        setConfig((current) => ({
            ...current,
            [section]: {
                ...current[section],
                [field]: {
                    ...current[section][field],
                    [key]: fieldValue,
                },
            },
        }));
    };

    const updateFeature = (field: string, fieldValue: boolean) => {
        setConfig((current) => ({
            ...current,
            features: {
                ...current.features,
                [field]: fieldValue,
            },
        }));
    };

    const openCreateCustomField = (section: CustomFieldSection) => {
        setEditingFieldId(null);
        setDraft({
            ...EMPTY_DRAFT,
            section,
        });
        setOptionInput("");
        setCustomFieldError("");
        setCustomFieldDialogOpen(true);
    };

    const openEditCustomField = (field: CustomFieldConfig) => {
        setEditingFieldId(field.id);
        setDraft({
            id: field.id,
            label: field.label,
            type: field.type,
            section: field.section,
            placeholder: field.placeholder || "",
            required: field.required,
            visible: field.visible,
            options: [...(field.options || [])],
        });
        setOptionInput("");
        setCustomFieldError("");
        setCustomFieldDialogOpen(true);
    };

    const closeCustomFieldDialog = () => {
        setCustomFieldDialogOpen(false);
        setEditingFieldId(null);
        setDraft({ ...EMPTY_DRAFT });
        setOptionInput("");
        setCustomFieldError("");
    };

    const addOption = () => {
        const option = optionInput.trim();
        if (!option) return;
        if (draft.options.some((item) => item.toLowerCase() === option.toLowerCase())) return;

        setDraft((current) => ({
            ...current,
            options: [...current.options, option],
        }));
        setOptionInput("");
    };

    const removeOption = (option: string) => {
        setDraft((current) => ({
            ...current,
            options: current.options.filter((item) => item !== option),
        }));
    };

    const saveCustomField = () => {
        const label = draft.label.trim();

        if (!label) {
            setCustomFieldError("Field label is required.");
            return;
        }

        const generatedId = slugify(label);

        if (!generatedId) {
            setCustomFieldError("Please enter a valid field label.");
            return;
        }

        const id = editingFieldId || generatedId;

        const existingOtherField = config.customFields.find(
            (field) => field.id === id && field.id !== editingFieldId
        );

        if (existingOtherField) {
            setCustomFieldError(`A custom field with the key "${id}" already exists.`);
            return;
        }

        if (["select", "radio"].includes(draft.type) && draft.options.length === 0) {
            setCustomFieldError("Add at least one option for dropdown or radio fields.");
            return;
        }

        const customField: CustomFieldConfig = {
            id,
            label,
            type: draft.type,
            section: draft.section,
            visible: draft.visible,
            required: draft.visible ? draft.required : false,
            placeholder: draft.placeholder.trim(),
            options: ["select", "radio"].includes(draft.type) ? draft.options : [],
        };

        setConfig((current) => ({
            ...current,
            customFields: editingFieldId
                ? current.customFields.map((field) => (field.id === editingFieldId ? customField : field))
                : [...current.customFields, customField],
        }));

        closeCustomFieldDialog();
    };

    const deleteCustomField = (id: string) => {
        const field = config.customFields.find((item) => item.id === id);
        if (!field) return;

        const confirmed = window.confirm(`Delete custom field "${field.label}"?`);
        if (!confirmed) return;

        setConfig((current) => ({
            ...current,
            customFields: current.customFields.filter((item) => item.id !== id),
        }));
    };

    const toggleCustomField = (id: string, key: "visible" | "required", checked: boolean) => {
        setConfig((current) => ({
            ...current,
            customFields: current.customFields.map((field) =>
                field.id === id
                    ? {
                          ...field,
                          [key]: checked,
                          ...(key === "visible" && !checked ? { required: false } : {}),
                      }
                    : field
            ),
        }));
    };

    const subjectCustomFields = useMemo(
        () => config.customFields.filter((field) => field.section === "subject"),
        [config.customFields]
    );

    const requestCustomFields = useMemo(
        () => config.customFields.filter((field) => field.section === "request"),
        [config.customFields]
    );

    const save = async () => {
        setSaving(true);
        try {
            await onSave(config);
        } finally {
            setSaving(false);
        }
    };

    const visibleCount = (section: Record<string, FieldConfig>) =>
        Object.values(section).filter((item) => item.visible).length;

    const ConfigFieldCard = ({
        section,
        field,
        label,
    }: {
        section: "fields" | "request";
        field: string;
        label: string;
    }) => {
        const item = config[section][field] || { visible: true, required: false };

        return (
            <Card className="h-full border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                <CardContent className="h-full p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <Typography variant="subtitle1" className="font-semibold text-gray-800">
                                {label}
                            </Typography>
                            <Typography variant="caption" className="break-all text-gray-400">
                                {field}
                            </Typography>
                        </div>
                        <div
                            className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                item.visible ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
                            }`}
                        >
                            {item.visible ? "Visible" : "Hidden"}
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={item.visible}
                                    onChange={(event) =>
                                        updateField(section, field, "visible", event.target.checked)
                                    }
                                />
                            }
                            label="Show field"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={item.required}
                                    disabled={!item.visible}
                                    onChange={(event) =>
                                        updateField(section, field, "required", event.target.checked)
                                    }
                                />
                            }
                            label="Required"
                        />
                    </div>
                </CardContent>
            </Card>
        );
    };

    const CustomFieldCard = ({ field }: { field: CustomFieldConfig }) => (
        <Card className="h-full border border-indigo-100 bg-indigo-50/30 shadow-sm transition-shadow hover:shadow-md">
            <CardContent className="h-full p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <Typography variant="subtitle1" className="font-semibold text-gray-900">
                                {field.label}
                            </Typography>
                            <Chip label="Custom" size="small" color="primary" variant="outlined" />
                        </div>
                        <Typography variant="caption" className="break-all text-gray-400">
                            {field.id} · {FIELD_TYPE_OPTIONS.find((item) => item.value === field.type)?.label}
                        </Typography>
                    </div>
                    <div className="flex items-center gap-1">
                        <IconButton size="small" onClick={() => openEditCustomField(field)}>
                            <PencilIcon className="h-5 w-5" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteCustomField(field.id)}>
                            <TrashIcon className="h-5 w-5" />
                        </IconButton>
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    <Chip
                        size="small"
                        icon={<EyeIcon className="!h-4 !w-4" />}
                        label={field.visible ? "Visible" : "Hidden"}
                        color={field.visible ? "success" : "default"}
                    />
                    {field.required && <Chip size="small" label="Required" color="warning" />}
                    {field.type === "select" && (
                        <Chip size="small" label={`${field.options?.length || 0} options`} />
                    )}
                </div>

                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={field.visible}
                                onChange={(event) =>
                                    toggleCustomField(field.id, "visible", event.target.checked)
                                }
                            />
                        }
                        label="Show field"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={field.required}
                                disabled={!field.visible}
                                onChange={(event) =>
                                    toggleCustomField(field.id, "required", event.target.checked)
                                }
                            />
                        }
                        label="Required"
                    />
                </div>
            </CardContent>
        </Card>
    );

    const CustomFieldsSection = ({
        section,
        fields,
        title,
        description,
    }: {
        section: CustomFieldSection;
        fields: CustomFieldConfig[];
        title: string;
        description: string;
    }) => (
        <Card className="mb-6 border border-gray-200 shadow-sm">
            <CardContent className="p-5">
                <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <Typography variant="h6" className="font-bold text-gray-900">
                                {title}
                            </Typography>
                            <Chip label={`${fields.length} custom`} size="small" />
                        </div>
                        <Typography variant="body2" className="mt-1 text-gray-500">
                            {description}
                        </Typography>
                    </div>
                    <Button
                        variant="contained"
                        startIcon={<span className="text-lg leading-none">+</span>}
                        onClick={() => openCreateCustomField(section)}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Add Custom Field
                    </Button>
                </div>

                {fields.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center">
                        <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                        <Typography variant="subtitle2" className="mt-2 font-semibold text-gray-700">
                            No custom fields yet
                        </Typography>
                        <Typography variant="body2" className="mt-1 text-gray-500">
                            Add a project-specific field without changing the built-in form fields.
                        </Typography>
                    </div>
                ) : (
                    <Grid container spacing={2}>
                        {fields.map((field) => (
                            <Grid item xs={12} sm={6} lg={4} key={field.id}>
                                <CustomFieldCard field={field} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </CardContent>
        </Card>
    );

    return (
        <div className="min-h-full bg-gray-50 p-4 md:p-6">
            <div className="mx-auto max-w-7xl">
                <div className="mb-5 flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-indigo-50 p-2 text-indigo-600">
                            <Cog6ToothIcon className="h-7 w-7" />
                        </div>
                        <div>
                            <Typography variant="h5" className="font-bold text-gray-900">
                                Service Request Form
                            </Typography>
                            <Typography variant="body2" className="text-gray-500">
                                Configure built-in fields, custom fields, validation and service request
                                features.
                            </Typography>
                        </div>
                    </div>

                    <Button
                        variant="contained"
                        size="large"
                        disabled={saving}
                        onClick={save}
                        startIcon={<CheckCircleIcon className="h-5 w-5" />}
                        sx={{ textTransform: "none", borderRadius: 2, minWidth: 190 }}
                    >
                        {saving ? "Saving..." : "Save Configuration"}
                    </Button>
                </div>

                <Grid container spacing={2} className="mb-6">
                    <Grid item xs={12} md={4}>
                        <Card className="border border-gray-200 shadow-sm">
                            <CardContent className="flex items-center gap-3 p-5">
                                <UserGroupIcon className="h-9 w-9 rounded-xl bg-blue-50 p-2 text-blue-600" />
                                <div>
                                    <Typography variant="h6" className="font-bold">
                                        {visibleCount(config.fields)} / {Object.keys(config.fields).length}
                                    </Typography>
                                    <Typography variant="body2" className="text-gray-500">
                                        Subject fields visible
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card className="border border-gray-200 shadow-sm">
                            <CardContent className="flex items-center gap-3 p-5">
                                <DocumentTextIcon className="h-9 w-9 rounded-xl bg-purple-50 p-2 text-purple-600" />
                                <div>
                                    <Typography variant="h6" className="font-bold">
                                        {visibleCount(config.request)} / {Object.keys(config.request).length}
                                    </Typography>
                                    <Typography variant="body2" className="text-gray-500">
                                        Request fields visible
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card className="border border-gray-200 shadow-sm">
                            <CardContent className="flex items-center gap-3 p-5">
                                <EyeIcon className="h-9 w-9 rounded-xl bg-green-50 p-2 text-green-600" />
                                <div>
                                    <Typography variant="h6" className="font-bold">
                                        {Object.values(config.features).filter(Boolean).length} /{" "}
                                        {Object.keys(config.features).length}
                                    </Typography>
                                    <Typography variant="body2" className="text-gray-500">
                                        Features enabled
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Card className="mb-6 border border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                        <div className="mb-5">
                            <Typography variant="h6" className="font-bold text-gray-900">
                                Subject Information
                            </Typography>
                            <Typography variant="body2" className="text-gray-500">
                                Choose which voter or subject information appears in the service request form.
                            </Typography>
                        </div>
                        <Grid container spacing={2}>
                            {SUBJECT_FIELDS.map(([field, label]) => (
                                <Grid item xs={12} sm={6} lg={4} key={field}>
                                    <ConfigFieldCard section="fields" field={field} label={label} />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                <CustomFieldsSection
                    section="subject"
                    fields={subjectCustomFields}
                    title="Custom Subject Fields"
                    description="Add project-specific information about the voter or subject."
                />

                <Card className="mb-6 border border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                        <div className="mb-5">
                            <Typography variant="h6" className="font-bold text-gray-900">
                                Service Request Details
                            </Typography>
                            <Typography variant="body2" className="text-gray-500">
                                Configure the fields used when creating and updating a service request.
                            </Typography>
                        </div>
                        <Grid container spacing={2}>
                            {REQUEST_FIELDS.map(([field, label]) => (
                                <Grid item xs={12} sm={6} lg={4} key={field}>
                                    <ConfigFieldCard section="request" field={field} label={label} />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                <CustomFieldsSection
                    section="request"
                    fields={requestCustomFields}
                    title="Custom Request Fields"
                    description="Add project-specific questions that should be captured with each service request."
                />

                <Card className="mb-6 border border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                        <div className="mb-5">
                            <Typography variant="h6" className="font-bold text-gray-900">
                                Service Request Level
                            </Typography>
                            <Typography variant="body2" className="text-gray-500">
                                Select how the Service Request Level options should behave in the form.
                            </Typography>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <button
                                type="button"
                                onClick={() =>
                                    updateField("fields", "serviceRequestLevel", "reference", false)
                                }
                                className={`rounded-xl border-2 p-5 text-left transition ${
                                    !config.fields.serviceRequestLevel?.reference
                                        ? "border-indigo-500 bg-indigo-50"
                                        : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-gray-900">Individual / Family</div>
                                        <div className="mt-1 text-sm text-gray-500">
                                            Shows Individual, Family and Community options.
                                        </div>
                                    </div>
                                    {!config.fields.serviceRequestLevel?.reference && (
                                        <CheckCircleIcon className="h-6 w-6 text-indigo-600" />
                                    )}
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    updateField("fields", "serviceRequestLevel", "reference", true)
                                }
                                className={`rounded-xl border-2 p-5 text-left transition ${
                                    config.fields.serviceRequestLevel?.reference
                                        ? "border-indigo-500 bg-indigo-50"
                                        : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-gray-900">Self / Reference</div>
                                        <div className="mt-1 text-sm text-gray-500">
                                            Shows Self, Reference and Community options.
                                        </div>
                                    </div>
                                    {config.fields.serviceRequestLevel?.reference && (
                                        <CheckCircleIcon className="h-6 w-6 text-indigo-600" />
                                    )}
                                </div>
                            </button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                        <div className="mb-5">
                            <Typography variant="h6" className="font-bold text-gray-900">
                                Form Features
                            </Typography>
                            <Typography variant="body2" className="text-gray-500">
                                Enable or disable additional service request functionality.
                            </Typography>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {FEATURE_FIELDS.map(([field, label, description]) => (
                                <div
                                    key={field}
                                    className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div>
                                        <Typography
                                            variant="subtitle1"
                                            className="font-semibold text-gray-800"
                                        >
                                            {label}
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-500">
                                            {description}
                                        </Typography>
                                    </div>
                                    <Switch
                                        checked={Boolean(config.features[field])}
                                        onChange={(event) => updateFeature(field, event.target.checked)}
                                    />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Dialog open={customFieldDialogOpen} onClose={closeCustomFieldDialog} fullWidth maxWidth="md">
                <DialogTitle>
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <Typography variant="h6" className="font-bold">
                                {editingFieldId ? "Edit Custom Field" : "Add Custom Field"}
                            </Typography>
                            <Typography variant="body2" className="text-gray-500">
                                Create a reusable field for this project's service request form.
                            </Typography>
                        </div>
                        <IconButton onClick={closeCustomFieldDialog}>
                            <span className="text-xl">×</span>
                        </IconButton>
                    </div>
                </DialogTitle>

                <DialogContent dividers>
                    <div className="space-y-5 pt-2">
                        <TextField
                            fullWidth
                            label="Field Label"
                            value={draft.label}
                            onChange={(event) =>
                                setDraft((current) => ({ ...current, label: event.target.value }))
                            }
                            placeholder="Example: Department Name"
                            helperText={
                                draft.label
                                    ? `Field key: ${editingFieldId || slugify(draft.label)}`
                                    : "Use a clear label clients will understand."
                            }
                        />

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <FormControl fullWidth>
                                <InputLabel>Field Type</InputLabel>
                                <Select
                                    label="Field Type"
                                    value={draft.type}
                                    onChange={(event) =>
                                        setDraft((current) => ({
                                            ...current,
                                            type: event.target.value as CustomFieldType,
                                        }))
                                    }
                                >
                                    {FIELD_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <div>
                                                <div className="font-medium">{option.label}</div>
                                                <div className="text-xs text-gray-500">
                                                    {option.description}
                                                </div>
                                            </div>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Form Section</InputLabel>
                                <Select
                                    label="Form Section"
                                    value={draft.section}
                                    onChange={(event) =>
                                        setDraft((current) => ({
                                            ...current,
                                            section: event.target.value as CustomFieldSection,
                                        }))
                                    }
                                >
                                    <MenuItem value="subject">Subject Information</MenuItem>
                                    <MenuItem value="request">Service Request Details</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        {draft.type !== "checkbox" && (
                            <TextField
                                fullWidth
                                label="Placeholder"
                                value={draft.placeholder}
                                onChange={(event) =>
                                    setDraft((current) => ({ ...current, placeholder: event.target.value }))
                                }
                                placeholder="Example: Select or enter a value"
                            />
                        )}

                        {(draft.type === "select" || draft.type === "radio") && (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                <Typography variant="subtitle2" className="mb-2 font-semibold">
                                    Field Options
                                </Typography>
                                <div className="flex gap-2">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={optionInput}
                                        onChange={(event) => setOptionInput(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                addOption();
                                            }
                                        }}
                                        placeholder="Type an option and press Add"
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={addOption}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Add
                                    </Button>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {draft.options.map((option) => (
                                        <Chip
                                            key={option}
                                            label={option}
                                            onDelete={() => removeOption(option)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {customFieldError && (
                            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {customFieldError}
                            </div>
                        )}

                        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <Typography variant="subtitle2" className="font-semibold">
                                    Field behavior
                                </Typography>
                                <Typography variant="body2" className="text-gray-500">
                                    Required fields must be completed before the request can be submitted.
                                </Typography>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={draft.visible}
                                            onChange={(event) =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    visible: event.target.checked,
                                                    required: event.target.checked ? current.required : false,
                                                }))
                                            }
                                        />
                                    }
                                    label="Visible"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={draft.required}
                                            disabled={!draft.visible}
                                            onChange={(event) =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    required: event.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label="Required"
                                />
                            </div>
                        </div>
                    </div>
                </DialogContent>

                <DialogActions className="p-4">
                    <Button onClick={closeCustomFieldDialog} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveCustomField}
                        disabled={
                            !draft.label.trim() ||
                            ((draft.type === "select" || draft.type === "radio") &&
                                draft.options.length === 0)
                        }
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        {editingFieldId ? "Update Field" : "Add Field"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ServiceRequestFormConfig;
